import * as React from 'react';
import { Fragment } from 'react';
import { compose, withHandlers, withState, withProps, lifecycle } from 'recompose';
import { translate } from 'react-i18next';
import { skin, OemProps } from 'ui/styles/skin';
import { TranslateProps } from 'util/i18n';

export type OwnProps = {
    index: number;
    max: number;
    checked: boolean;
    textKey: string;
    position?: number;
    onCheckChanged: (index: number, checked: boolean) => void;
    onPositionChanged: (index: number, position: number) => void;
};

const RawColumnPositionControl = ({
    index,
    max,
    checked,
    positionString,
    textKey,
    handleCheckChange,
    handlePositionChange,
    styles,
    t,
}: Props) =>
    <Fragment>
        <input
            id={ `column-position-control-${index}-visible` }
            type="checkbox"
            checked={ checked }
            onChange={ handleCheckChange }
        />
        <label
            htmlFor={ `column-position-control-${index}-visible` }
        >
            { t(textKey) }
        </label>
        <input
            id={ `column-position-control-${index}-position` }
            type="number"
            value={ checked ? positionString : '' }
            min={ 1 }
            max={ max }
            onChange={ handlePositionChange }
            style={ checked ? styles.enabled : styles.disabled }
        />
    </Fragment>
;

type RecomposeProps = {
    positionString: string;
    handleCheckChange: React.ChangeEventHandler<HTMLInputElement>;
    handlePositionChange: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setPositionString: (s: string) => void;
};

const positionToString = (position: number | undefined): string =>
    typeof position === 'undefined'
        ? ''
        : `${position + 1}`
;

type Props = RecomposeProps & TranslateProps & OemProps & OwnProps;

export const ColumnPositionControl = compose<Props, OwnProps>(
    skin,
    translate(),
    withState('positionString', '_setPositionString', ({ position }: OwnProps) => positionToString(position)),
    withHandlers<OwnProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleCheckChange: ({ index, onCheckChanged, position, _setPositionString }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const { checked } = e.currentTarget;
            onCheckChanged(index, checked);
        },
        handlePositionChange: ({ index, onPositionChanged, checked, onCheckChanged, max }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const position = Math.max(1, Math.min(parseInt(e.currentTarget.value, 10), max));

            // Report the new position
            if (!isNaN(position)) {
                onPositionChanged(index, position - 1); // Convert back to 0-based index
            }
        },
    }),
    withProps(({ styles }: OemProps) => ({
        styles: {
            ...styles,
            enabled: {
                ...styles.Input,
                transition: 'opacity .2s ease-out',
                opacity: 1,
            },
            disabled: {
                ...styles.Input,
                transition: 'opacity .2s ease-out',
                opacity: .5,
            },
        },
    })),
    lifecycle({
        componentDidUpdate(oldProps: Props & _RecomposeProps) {
            // Update our internal representation of the position if our parent gave us a new position
            // (e.g. if we got shifted around because another column got shown/hidden/moved)
            const { position, _setPositionString } = this.props;
            if (oldProps.position !== position) {
                const positionString = positionToString(position);
                _setPositionString(positionString);
            }
        },
    }),
)(RawColumnPositionControl);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/tableLayout/ColumnPositionControl.tsx